/**
 * EVO — marshrutlar daraxti (kod asosida, fayl generatsiyasisiz).
 *
 * Nima uchun kod asosida:
 *   • Qo'shimcha kodgeneratsiya bosqichi yo'q — `routeTree.gen.ts` ni git'ga
 *     qo'shish/qo'shmaslik bahsi ham yo'q.
 *   • 1-2 dev uchun marshrutlar soni ko'p emas, hammasi bitta faylda ko'rinadi.
 *   • Tiplashtirish bir xil: `Register` orqali `Link`/`navigate` tekshiriladi.
 *
 * Tuzilish:
 *   root
 *   ├── /login        — ochiq
 *   └── _protected    — yo'lsiz (pathless) qatlam: ProtectedRoute + AppShell
 *       ├── /                 — bosh sahifa
 *       ├── /products         — mahsulotlar ro'yxati
 *       ├── /products/new     — yangi mahsulot
 *       ├── /products/$id     — mahsulot kartochkasi
 *       ├── /customers        — mijozlar ro'yxati
 *       ├── /customers/$id    — mijoz kartochkasi
 *       ├── /inventory/…      — kundalik ombor ekranlari (qoldiq, kirim,
 *       │                       chiqim, ko'chirish, inventarizatsiya, daftar)
 *       ├── /sales/orders     — sotuv buyurtmalari
 *       ├── /sales/orders/new — yangi buyurtma (klaviatura bilan kiritish)
 *       ├── /sales/orders/$id — buyurtma kartochkasi
 *       ├── /reports/…        — to'qqizta hisobot (MVP_PLAN §2.8)
 *       └── /loyalty/…        — usta dasturi: majburiyat holati, ball
 *                               qoidalari, QR partiyalar, sovg'a katalogi,
 *                               almashtirishlar, ustalar, nazorat navbati
 *
 * Yangi ekran qo'shish: `protectedRoute` ostiga `createRoute` yozib,
 * `routeTree` ga qo'shiladi va `config/menu.ts` ga ruxsat kodi bilan kiritiladi.
 * Ekran komponenti `lazyRouteComponent` orqali ulanadi — sabab quyida.
 *
 * ============================================================================
 * KOD BO'LISH (code splitting) — QOIDA
 * ============================================================================
 * Bitta bundle'da kirish sahifasini ochgan sotuvchi 11 ta hisobot ekranini,
 * import ustasini va usta dasturining boshqaruv ekranlarini ham yuklab olardi —
 * ularning birortasini hech qachon ochmasa ham. O'zbekistondagi mobil internet
 * uchun bu sezilarli: 220 kB gzip'ning katta qismi shu foydalanilmagan ekranlar.
 *
 * Shuning uchun QOIDA BITTA va u BARCHA marshrutlarga tegishli:
 *
 *   Har bir ekran moduli `lazyRouteComponent` orqali ulanadi.
 *
 * Yarmi kechiktirilgan, yarmi darhol yuklanadigan holat ATAYLAB yo'q: ikki
 * uslub bir kod bazasida yashasa, keyingi dasturchi qaysi birini tanlashni
 * bilmaydi va vaqt o'tib ikkalasi ham buziladi.
 *
 * ---------------------------------------------------------------------------
 * YAGONA ISTISNO: `/login` va ildiz qatlami
 * ---------------------------------------------------------------------------
 * `LoginPage`, `ProtectedRoute`, `AppShell` va `NotFoundPage` TO'G'RIDAN-TO'G'RI
 * import qilinadi. Asos:
 *
 *   • `/login` — sessiyasi yo'q har bir foydalanuvchining BIRINCHI ekrani.
 *     Uni chunk'ga chiqarish hech narsa tejamaydi (baribir kerak), lekin
 *     eng muhim sahifaga yana bitta ketma-ket so'rov qo'shadi: avval
 *     `index.js`, keyin `login.js`. Sekin tarmoqda bu ikki marta kutish.
 *   • `ProtectedRoute` + `AppShell` — ildiz qatlami: HAR QANDAY manzilda
 *     render bo'ladi, ya'ni «kerak bo'lmasligi mumkin» degan holat yo'q.
 *   • `NotFoundPage` — marshrut EMAS, u router'ning zaxira komponenti.
 *     Uni kechiktirish «sahifa topilmadi» ni ko'rsatish uchun yana bitta
 *     tarmoq so'rovini talab qilardi — aynan tarmoq ishonchsiz paytda.
 *
 * Qolgan HAMMA marshrut — istisnosiz kechiktirilgan.
 *
 * Yuklanish va xato holatlari router darajasida bir marta ulangan
 * (`defaultPendingComponent` / `defaultErrorComponent`, quyida) — yangi ekran
 * qo'shgan dasturchi ularni ulashni UNUTA OLMAYDI.
 */
import type { ReactNode } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type RouteComponent,
} from '@tanstack/react-router';
import { AppShell } from '../components/layout/app-shell';
import { PartnerViewProvider } from '../components/layout/partner-view';
import { ProtectedRoute } from '../components/protected-route';
import { RouteError, RoutePending } from '../components/route-boundary';
import { LoginPage } from './login';
import { NotFoundPage } from './not-found';
import { parseListSearch } from '../lib/list-search';
import { parseProductSearch } from '../lib/products';
import { parseAgentActivitySearch } from '../lib/agent-search';
import { parseDeliverySearch } from '../lib/delivery-search';
import { parsePartnerSupplySearch } from '../lib/partner-supply-search';
import {
  parseMovementSearch,
  parseSalesOrderSearch,
  parseStockSearch,
} from '../lib/ops-search';
import {
  parseAbcReportSearch,
  parseAgingReportSearch,
  parseDisciplineReportSearch,
  parseContainerReportSearch,
  parseDeadStockReportSearch,
  parseLtvReportSearch,
  parsePeriodSearch,
  parseProfitReportSearch,
  parseSalesReportSearch,
  parseShipmentReportSearch,
  parseStockAgeReportSearch,
  parseSupplierScorecardSearch,
  parseXyzReportSearch,
} from '../lib/report-search';
import {
  parseFraudSearch,
  parseInstallerSearch,
  parsePointRuleSearch,
  parseQrBatchSearch,
  parseRedemptionSearch,
} from '../lib/loyalty-search';
import {
  parseBonusLedgerSearch,
  parseBonusSearch,
  parseDealerSearch,
} from '../lib/dealer-search';
import {
  parseAuditSearch,
  parseGoodsReceiptSearch,
  parseImportSearch,
  parsePriceListSearch,
  parsePurchaseOrderSearch,
  parseReorderRunSearch,
  parseShipmentSearch,
  parseSupplierSearch,
} from '../lib/procurement-search';
// Xodimlar — komissiya va KPI URL holati (`lib/hr.ts` da, chunki ular
// xarid/sozlama ekranlariga umuman bog'liq emas).
import { parseCommissionRunSearch, parseKpiSearch } from '../lib/hr';
import {
  parseCreditNoteSearch,
  parseFxRateSearch,
  parseInvoiceSearch,
  parsePayablesSearch,
  parsePaymentSearch,
  parseReceivablesSearch,
  parseSupplierPaymentSearch,
} from '../lib/finance-search';
import { parseEmployeeSearch } from '../lib/employees';
import { parseQuarantineSearch, parseReturnSearch } from '../lib/returns-search';
import {
  parseBalanceSheetSearch,
  parseJournalsSearch,
  parsePeriodsSearch,
} from '../lib/accounting-search';

// ---------------------------------------------------------------------------
// Kechiktirilgan ekranlar
// ---------------------------------------------------------------------------

/*
 * `lazyRouteComponent(() => import(…), 'ExportNomi')` — router'ning o'z usuli,
 * `React.lazy` emas. Farqi muhim:
 *
 *   • Router chunk'ni RENDER'dan oldin, marshrut yuklash bosqichida so'raydi
 *     (`component.preload()`). Ya'ni navigatsiyada eski ekran joyida turadi va
 *     yangi ekran TAYYOR bo'lganda almashadi — spinner miltillamaydi.
 *   • `defaultPreload: 'intent'` shu `preload` ga tayanadi: havola ustiga
 *     sichqoncha kelganda chunk allaqachon yo'lda bo'ladi.
 *   • Modul topilmasa (deploy'dan keyin eski chunk yo'qolgan bo'lsa) sahifani
 *     bir marta o'zi qayta yuklaydi — `sessionStorage` bilan halqadan himoya.
 *
 * Chunk nomlari fayl yo'lidan kelib chiqadi; `lib/reports.ts` kabi bir necha
 * ekranga umumiy modullarni Rollup o'zi alohida umumiy chunk'ga chiqaradi.
 */

const DashboardPage = lazyRouteComponent(() => import('./dashboard'), 'DashboardPage');

const ProductsListPage = lazyRouteComponent(
  () => import('./products/products-list'),
  'ProductsListPage',
);
const ProductNewPage = lazyRouteComponent(() => import('./products/product-new'), 'ProductNewPage');
const ProductDetailPage = lazyRouteComponent(
  () => import('./products/product-detail'),
  'ProductDetailPage',
);

const CustomersListPage = lazyRouteComponent(
  () => import('./customers/customers-list'),
  'CustomersListPage',
);
const CustomerNewPage = lazyRouteComponent(
  () => import('./customers/customer-new'),
  'CustomerNewPage',
);
const CustomerDetailPage = lazyRouteComponent(
  () => import('./customers/customer-detail'),
  'CustomerDetailPage',
);

const SuppliersPage = lazyRouteComponent(
  () => import('./partners/suppliers'),
  'SuppliersPage',
);

/*
 * Moliya ekranlari — kundalik pul ishi.
 *
 * To'rtalasi ALOHIDA chunk: moliyachi ular orasida yuradi, sotuvchi esa
 * faqat `/invoices` ni ochadi (qolganlari unga menyuda ham ko'rinmaydi).
 * Bitta «finance» modulga birlashtirilsa, sotuvchi to'lov formasini,
 * kurs tarixini va AP hisobotini ham yuklab olardi.
 */
const InvoicesPage = lazyRouteComponent(() => import('./finance/invoices'), 'InvoicesPage');
const PaymentsPage = lazyRouteComponent(() => import('./finance/payments'), 'PaymentsPage');
const ReceivablesPage = lazyRouteComponent(
  () => import('./finance/receivables'),
  'ReceivablesPage',
);
const PayablesPage = lazyRouteComponent(() => import('./finance/payables'), 'PayablesPage');
/*
 * Ta'minotchiga to'lov — `/payables` dan ALOHIDA chunk va alohida ekran.
 *
 * `/payables` HISOBOT kesimi: «kimga qancha qarzmiz», eksport qilinadi va
 * yopiladi. Bu esa AMAL ekrani: ochiq invoyslar ro'yxati va ulardan pul
 * chiqarish formasi (`payment.supplier`). Ikkalasini birlashtirish hisobotni
 * to'lov formasi bilan og'irlashtirardi — u esa rahbarda ham ochiladi va
 * unda bu ruxsat YO'Q.
 */
const SupplierPaymentsPage = lazyRouteComponent(
  () => import('./finance/supplier-payments'),
  'SupplierPaymentsPage',
);
const FxRatesPage = lazyRouteComponent(() => import('./finance/fx-rates'), 'FxRatesPage');
/*
 * Kun yopish va filial xarajati — `search` holatisiz ekranlar.
 *
 * Boshqa moliya ekranlaridan farqi shu: ular filtrni URL da yuritadi
 * (`parse*Search`), bu ikkitasi esa yuritmaydi va bu ataylab. Kassir ekranni
 * kuniga bir marta ochadi va bitta kunni yopadi — URL ga saqlanadigan holat
 * yo'q. Filtr qo'shilsa `lib/finance-search.ts` ga parser yoziladi.
 */
const DayClosingPage = lazyRouteComponent(
  () => import('./finance/day-closing'),
  'DayClosingPage',
);
const ExpensesPage = lazyRouteComponent(() => import('./finance/expenses'), 'ExpensesPage');
/*
 * Qayta baholash ham `search` holatisiz va bu ataylab.
 *
 * Ekran «hisoblash → ko'rish → yozish» oqimi bilan ishlaydi: URL ga
 * saqlangan tanlov sahifa yangilanganda hisobni O'ZI qayta ishga
 * tushirardi va buxgalter so'ramagan so'rov serverni kechib chiqardi.
 */
const FxRevaluationPage = lazyRouteComponent(
  () => import('./finance/fx-revaluation'),
  'FxRevaluationPage',
);

const StockPage = lazyRouteComponent(() => import('./inventory/stock'), 'StockPage');
const AvailabilityPage = lazyRouteComponent(
  () => import('./inventory/availability'),
  'AvailabilityPage',
);
const MovementsPage = lazyRouteComponent(() => import('./inventory/movements'), 'MovementsPage');
// Kirim va chiqim bitta modulda (bir xil forma, boshqa yo'nalish) — ikkalasi
// ham AYNAN o'sha chunk'ni so'raydi, ya'ni ikkinchisi tekinga keladi.
const ReceiptPage = lazyRouteComponent(() => import('./inventory/manual-movement'), 'ReceiptPage');
const IssuePage = lazyRouteComponent(() => import('./inventory/manual-movement'), 'IssuePage');
const TransfersPage = lazyRouteComponent(() => import('./inventory/transfers'), 'TransfersPage');
const CountsPage = lazyRouteComponent(() => import('./inventory/counts'), 'CountsPage');

/**
 * Kiruvchi yuk — ta'minotchi bizga yozgan xabarnomalar.
 *
 * Ekran BIZNING ilovamizda turadi, lekin uni faqat ta'minot bo'limi ochiq
 * bo'lgan tashkilot ko'radi: kod `supply.notice.view` bizning rollarimizda
 * yo'q va `filterMenu` bandni ko'rsatmaydi.
 */
const IncomingSupplyPage = lazyRouteComponent(
  () => import('./supply/incoming'),
  'IncomingSupplyPage',
);

const SalesOrdersListPage = lazyRouteComponent(
  () => import('./sales/orders-list'),
  'SalesOrdersListPage',
);
const SalesOrderNewPage = lazyRouteComponent(
  () => import('./sales/order-new'),
  'SalesOrderNewPage',
);
const SalesOrderDetailPage = lazyRouteComponent(
  () => import('./sales/order-detail'),
  'SalesOrderDetailPage',
);

const ReturnsListPage = lazyRouteComponent(() => import('./sales/returns-list'), 'ReturnsListPage');
const ReturnDetailPage = lazyRouteComponent(
  () => import('./sales/return-detail'),
  'ReturnDetailPage',
);
const ReturnsQuarantinePage = lazyRouteComponent(
  () => import('./sales/returns-quarantine'),
  'ReturnsQuarantinePage',
);
const DeliveriesPage = lazyRouteComponent(() => import('./sales/deliveries'), 'DeliveriesPage');
/*
 * Do'kon kassasi — sotuvchining ENG KO'P ochadigan ikkita ekrani.
 *
 * `lazyRouteComponent` baribir qo'llanadi (qolgan ekranlar bilan bir xil
 * naqsh): kassa ekrani sotuvchida kuniga bir marta ochiladi va o'sha
 * ochilishdan keyin kesh ishlaydi, boshqa rollarga esa u UMUMAN
 * yuklanmasligi kerak — bundle'ni ular uchun kattalashtirishning ma'nosi yo'q.
 */
const DirectSalePage = lazyRouteComponent(() => import('./sales/direct-sale'), 'DirectSalePage');
const MyCashPage = lazyRouteComponent(() => import('./sales/my-cash'), 'MyCashPage');

/**
 * Ta'minot bo'limining ish maydoni — bo'lim xodimi kun boshida ochadigan ekran.
 *
 * `lazyRouteComponent` qolgan ekranlar bilan bir xil naqshda: ekran faqat
 * ta'minot va moliya rollariga ochiq (`po.view` + `supplier.view`), ya'ni
 * uni asosiy to'plamga qo'shish sotuvchi va omborchiga hech qachon kerak
 * bo'lmaydigan kodni yuklatardi.
 */
const ProcurementWorkspacePage = lazyRouteComponent(
  () => import('./procurement/workspace'),
  'ProcurementWorkspacePage',
);
const PurchaseOrdersListPage = lazyRouteComponent(
  () => import('./procurement/orders-list'),
  'PurchaseOrdersListPage',
);
const PurchaseOrderNewPage = lazyRouteComponent(
  () => import('./procurement/order-new'),
  'PurchaseOrderNewPage',
);
const PurchaseOrderDetailPage = lazyRouteComponent(
  () => import('./procurement/order-detail'),
  'PurchaseOrderDetailPage',
);
const ReorderRunsPage = lazyRouteComponent(
  () => import('./procurement/reorder-runs'),
  'ReorderRunsPage',
);
/*
 * Xodimlar — komissiya va KPI (TZ v8.0 §25, §19).
 *
 * `lazyRouteComponent`: bu ekranlar kunda bir marta ochiladi (oy yopilganda),
 * ya'ni ularni asosiy to'plamga qo'shish har foydalanuvchiga hech qachon
 * kerak bo'lmasligi mumkin bo'lgan kodni yuklatardi.
 */
const CommissionRunsPage = lazyRouteComponent(
  () => import('./hr/commission-runs'),
  'CommissionRunsPage',
);
const CommissionRunDetailPage = lazyRouteComponent(
  () => import('./hr/commission-run-detail'),
  'CommissionRunDetailPage',
);
const KpiPage = lazyRouteComponent(() => import('./hr/kpi'), 'KpiPage');
/*
 * Xodimlar — `search` holatisiz: filtr (qidiruv, holat) ekranning ichida
 * yashaydi va URL ga saqlanmaydi. Kadrlar ro'yxati havola qilib
 * yuboriladigan ekran emas — u ochiladi va ishlatiladi.
 */
const EmployeesPage = lazyRouteComponent(() => import('./hr/employees'), 'EmployeesPage');
const PayrollPage = lazyRouteComponent(() => import('./hr/payroll'), 'PayrollPage');
const ControlPage = lazyRouteComponent(() => import('./hr/control'), 'ControlPage');
const DiscountsPage = lazyRouteComponent(() => import('./reports/discounts'), 'DiscountsPage');
const CreditNotesPage = lazyRouteComponent(() => import('./finance/credit-notes'), 'CreditNotesPage');
const TreasuryPage = lazyRouteComponent(() => import('./finance/treasury'), 'TreasuryPage');
const MySettingsPage = lazyRouteComponent(() => import('./settings/my-settings'), 'MySettingsPage');
const DealerControlPage = lazyRouteComponent(() => import('./dealers/dealer-control'), 'DealerControlPage');
/*
 * Ehson fondi MOLIYA ostida, xodimlar emas: u foydadan ajratma va
 * majburiyat, kadrlar ishi emas. Menyuda ham «Moliya» guruhida.
 */
const CharityPage = lazyRouteComponent(() => import('./finance/charity'), 'CharityPage');
const ReorderRunDetailPage = lazyRouteComponent(
  () => import('./procurement/reorder-run-detail'),
  'ReorderRunDetailPage',
);
const ShipmentsListPage = lazyRouteComponent(
  () => import('./procurement/shipments-list'),
  'ShipmentsListPage',
);
const ShipmentDetailPage = lazyRouteComponent(
  () => import('./procurement/shipment-detail'),
  'ShipmentDetailPage',
);
const CostingPage = lazyRouteComponent(
  () => import('./procurement/costing'),
  'CostingPage',
);
const GoodsReceiptsPage = lazyRouteComponent(
  () => import('./procurement/receipts'),
  'GoodsReceiptsPage',
);
const SupplierInvoicesPage = lazyRouteComponent(
  () => import('./procurement/supplier-invoices'),
  'SupplierInvoicesPage',
);
const ImportWizardPage = lazyRouteComponent(
  () => import('./import/import-wizard'),
  'ImportWizardPage',
);

/*
 * Ma'lumotnoma boshqaruvi — kategoriya, brend, o'lchov birligi (2026-08-15).
 * API 0000 dan beri bor edi, ekran esa yo'q edi (fayl sarlavhasidagi ramka).
 */
const ReferenceAdminPage = lazyRouteComponent(
  () => import('./reference/reference-admin'),
  'ReferenceAdminPage',
);

const UsersSettingsPage = lazyRouteComponent(() => import('./settings/users'), 'UsersSettingsPage');
const RolesSettingsPage = lazyRouteComponent(() => import('./settings/roles'), 'RolesSettingsPage');
const PriceListsSettingsPage = lazyRouteComponent(
  () => import('./settings/price-lists'),
  'PriceListsSettingsPage',
);
const CashAccountsSettingsPage = lazyRouteComponent(
  () => import('./settings/cash-accounts'),
  'CashAccountsSettingsPage',
);
const CashFlowItemsSettingsPage = lazyRouteComponent(
  () => import('./settings/cash-flow-items'),
  'CashFlowItemsSettingsPage',
);
/**
 * Partner tashkilotlar — ro'yxat va paket muharriri.
 *
 * Muharrir menyuda YO'Q va bu ataylab: u ro'yxatdagi satrdan ochiladi va
 * o'zi yolg'iz ma'noga ega emas — qaysi tashkilot ekani manzilda turadi.
 */
const OrganizationsSettingsPage = lazyRouteComponent(
  () => import('./settings/organizations'),
  'OrganizationsSettingsPage',
);
const OrganizationModulesPage = lazyRouteComponent(
  () => import('./settings/organization-modules'),
  'OrganizationModulesPage',
);
/** «EVO'dan so'rash» — AI moduli uchun yagona ekran. */
const AiAskPage = lazyRouteComponent(() => import('./ai/ask'), 'AiAskPage');
const PrintTemplatesSettingsPage = lazyRouteComponent(
  () => import('./settings/print-templates'),
  'PrintTemplatesSettingsPage',
);
/** Aylanma-qoldiq vedomosti va daftar ↔ operatsiya sverkasi. */
const TrialBalancePage = lazyRouteComponent(
  () => import('./accounting/trial-balance'),
  'TrialBalancePage',
);
const ReconciliationPage = lazyRouteComponent(
  () => import('./accounting/reconciliation'),
  'ReconciliationPage',
);
const SystemSettingsPage = lazyRouteComponent(
  () => import('./settings/system'),
  'SystemSettingsPage',
);
/** Tashkiliy tuzilma — filiallar daraxti va «yangi filial» sehrgari. */
const OrganizationSettingsPage = lazyRouteComponent(
  () => import('./settings/organization'),
  'OrganizationSettingsPage',
);
/** «Xavfsizlik» bo'limi — `settings/audit.tsx` dan ko'chgan yagona ekran. */
const SecurityLogPage = lazyRouteComponent(() => import('./security/log'), 'SecurityLogPage');

// Tasdiqlash dvigateli — navbat va uning ikki sozlama ekrani (TZ v8.0 §12).
const ApprovalsPage = lazyRouteComponent(() => import('./approvals/index'), 'ApprovalsPage');
const ApprovalRulesSettingsPage = lazyRouteComponent(
  () => import('./settings/approval-rules'),
  'ApprovalRulesSettingsPage',
);
const DelegationsSettingsPage = lazyRouteComponent(
  () => import('./settings/delegations'),
  'DelegationsSettingsPage',
);

const ReportsHubPage = lazyRouteComponent(() => import('./reports/hub'), 'ReportsHubPage');
const StockReportPage = lazyRouteComponent(() => import('./reports/stock'), 'StockReportPage');
const StockValuationReportPage = lazyRouteComponent(
  () => import('./reports/stock-valuation'),
  'StockValuationReportPage',
);
const SalesReportPage = lazyRouteComponent(() => import('./reports/sales'), 'SalesReportPage');
const AgentActivityReportPage = lazyRouteComponent(
  () => import('./reports/agent-activity'),
  'AgentActivityReportPage',
);
const GrossProfitReportPage = lazyRouteComponent(
  () => import('./reports/gross-profit'),
  'GrossProfitReportPage',
);
const CustomerDisciplineReportPage = lazyRouteComponent(
  () => import('./reports/customer-discipline'),
  'CustomerDisciplineReportPage',
);

const ArAgingReportPage = lazyRouteComponent(
  () => import('./reports/ar-aging'),
  'ArAgingReportPage',
);
const CashFlowReportPage = lazyRouteComponent(
  () => import('./reports/cash-flow'),
  'CashFlowReportPage',
);
const DeadStockReportPage = lazyRouteComponent(
  () => import('./reports/dead-stock'),
  'DeadStockReportPage',
);
const ShipmentProfitabilityPage = lazyRouteComponent(
  () => import('./reports/shipment-profitability'),
  'ShipmentProfitabilityPage',
);
const ReorderReportPage = lazyRouteComponent(() => import('./reports/reorder'), 'ReorderReportPage');

/*
 * TAHLIL hisobotlari (TZ v4.0 §28 / CR-007, v8.0 §38–39).
 *
 * Ular ham alohida chunk: sakkiztasi ham katta jadval va formula izohlarini
 * olib yuradi, lekin foydalanuvchi ularni bir vaqtda ochmaydi. Bazaviy
 * hisobotlar bilan bitta bundle'ga qo'shish har ekranni og'irlashtirardi.
 */
const AbcReportPage = lazyRouteComponent(() => import('./reports/abc-analysis'), 'AbcReportPage');
const XyzReportPage = lazyRouteComponent(() => import('./reports/xyz-analysis'), 'XyzReportPage');
const RfmReportPage = lazyRouteComponent(() => import('./reports/rfm'), 'RfmReportPage');
const LtvReportPage = lazyRouteComponent(() => import('./reports/ltv'), 'LtvReportPage');
const SupplierScorecardPage = lazyRouteComponent(
  () => import('./reports/supplier-scorecard'),
  'SupplierScorecardPage',
);
const ContainerEfficiencyPage = lazyRouteComponent(
  () => import('./reports/container-efficiency'),
  'ContainerEfficiencyPage',
);
const StockAgeReportPage = lazyRouteComponent(
  () => import('./reports/stock-age'),
  'StockAgeReportPage',
);
const CustomerProfitabilityPage = lazyRouteComponent(
  () => import('./reports/customer-profitability'),
  'CustomerProfitabilityPage',
);
/*
 * Hujjatlar (DMS) — MODULDAGI YAGONA ekran.
 *
 * Qolgan hamma joyda hujjat `<DocumentPanel>` orqali OBYEKT kartochkasida
 * ko'rinadi (`components/document-panel.tsx` sarlavhasi). Bu ro'yxat esa
 * boshqa savolga javob beradi — «bugun nima tugayapti» — va u obyektdan
 * emas, vaqtdan kelib chiqadi.
 */
const ExpiringDocumentsPage = lazyRouteComponent(
  () => import('./documents/expiring'),
  'ExpiringDocumentsPage',
);
const LandedCostReportPage = lazyRouteComponent(
  () => import('./reports/landed-cost'),
  'LandedCostReportPage',
);

/*
 * Buxgalteriya — moliyaviy hisobotlar, daftar va davrlar.
 *
 * Uch hisobot `/reports/…` ostida turadi (boshqa hisobotlar bilan bir
 * manzil oilasida), daftar va davrlar esa `/accounting/…` ostida: ular
 * hisobot emas, ISH ekranlari — davr yopiladi, provodka ochiladi.
 * Menyuda esa hammasi bitta «Buxgalteriya» guruhida (`config/menu.ts`).
 */
const IncomeStatementPage = lazyRouteComponent(
  () => import('./reports/income-statement'),
  'IncomeStatementPage',
);
const BalanceSheetPage = lazyRouteComponent(
  () => import('./reports/balance-sheet'),
  'BalanceSheetPage',
);
const EquityReportPage = lazyRouteComponent(() => import('./reports/equity'), 'EquityReportPage');
const AccountsSettingsPage = lazyRouteComponent(
  () => import('./settings/accounts'),
  'AccountsSettingsPage',
);
const AccountingPeriodsPage = lazyRouteComponent(
  () => import('./accounting/periods'),
  'AccountingPeriodsPage',
);
const AccountingJournalsPage = lazyRouteComponent(
  () => import('./accounting/journals'),
  'AccountingJournalsPage',
);
const JournalDetailPage = lazyRouteComponent(
  () => import('./accounting/journal-detail'),
  'JournalDetailPage',
);

const LoyaltyStatusPage = lazyRouteComponent(() => import('./loyalty/status'), 'LoyaltyStatusPage');
const LoyaltyPointRulesPage = lazyRouteComponent(
  () => import('./loyalty/point-rules'),
  'LoyaltyPointRulesPage',
);
const LoyaltyQrBatchesPage = lazyRouteComponent(
  () => import('./loyalty/qr-batches'),
  'LoyaltyQrBatchesPage',
);
const LoyaltyRewardsPage = lazyRouteComponent(
  () => import('./loyalty/rewards'),
  'LoyaltyRewardsPage',
);
const LoyaltyRedemptionsPage = lazyRouteComponent(
  () => import('./loyalty/redemptions'),
  'LoyaltyRedemptionsPage',
);
const LoyaltyInstallersPage = lazyRouteComponent(
  () => import('./loyalty/installers'),
  'LoyaltyInstallersPage',
);
const LoyaltyInstallerDetailPage = lazyRouteComponent(
  () => import('./loyalty/installer-detail'),
  'LoyaltyInstallerDetailPage',
);
const LoyaltyFraudPage = lazyRouteComponent(() => import('./loyalty/fraud'), 'LoyaltyFraudPage');

/*
 * Diller kanali va bonus — `routes/dealers/` da.
 *
 * Papka `loyalty/` DAN ALOHIDA, garchi menyuda ikkalasi bir guruhda bo'lsa
 * ham: bu ekranlar `lib/dealer.ts` ga tayanadi va `lib/loyalty.ts` ning
 * butun tipini tortib kelmasligi kerak (chunk hajmi).
 */
const DealersPage = lazyRouteComponent(() => import('./dealers/dealers-list'), 'DealersPage');
const BonusAccountsPage = lazyRouteComponent(
  () => import('./dealers/bonus-accounts'),
  'BonusAccountsPage',
);
const BonusLedgerPage = lazyRouteComponent(
  () => import('./dealers/bonus-ledger'),
  'BonusLedgerPage',
);

/**
 * Kechiktirilgan ekranni URL holatiga bog'lovchi o'ram uchun `preload` ko'prigi.
 *
 * Ba'zi marshrutlarda komponent `search`/`params` ni prop sifatida oladi, ya'ni
 * router'ga to'g'ridan-to'g'ri kechiktirilgan komponent emas, kichik o'ram
 * funksiya beriladi. Muammo shu yerda: router chunk'ni `component.preload()`
 * orqali oldindan yuklaydi, oddiy o'ram funksiyada esa `preload` YO'Q. Natijada
 * chunk faqat render paytida so'ralardi — ekran spinner bilan «sakrardi» va
 * `defaultPreload: 'intent'` shu marshrutlarda ishlamay qolardi.
 *
 * Shuning uchun o'ramga ichkaridagi komponentning `preload` i ko'chiriladi:
 * router uchun o'ram ham xuddi kechiktirilgan komponentdek ko'rinadi.
 */
interface Preloadable {
  readonly preload?: (() => Promise<void>) | undefined;
}

function lazyPage(page: Preloadable, wrapper: () => ReactNode): RouteComponent {
  return Object.assign(wrapper, { preload: page.preload });
}

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

/** Login'dan keyin qaytish manzili — `?redirect=/stock` ko'rinishida. */
interface LoginSearch {
  readonly redirect?: string;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    // Faqat ichki yo'l qabul qilinadi. `//evil.com` yoki `https://…` bo'lsa
    // tashlab yuboriladi — bu ochiq redirect (open redirect) zaifligi.
    redirect:
      typeof search.redirect === 'string' &&
      search.redirect.startsWith('/') &&
      !search.redirect.startsWith('//')
        ? search.redirect
        : undefined,
  }),
  component: LoginRouteComponent,
});

function LoginRouteComponent(): ReturnType<typeof LoginPage> {
  const { redirect } = loginRoute.useSearch();
  return <LoginPage {...(redirect ? { redirectTo: redirect } : {})} />;
}

/**
 * Yo'lsiz (pathless) qatlam: URL'ga ta'sir qilmaydi, lekin barcha ichki
 * marshrutlarni himoya va karkas bilan o'raydi. Yangi ekran qo'shganda
 * himoyani unutish MUMKIN EMAS — u ota-marshrutdan keladi.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  component: ProtectedLayout,
});

function ProtectedLayout(): ReturnType<typeof AppShell> {
  return (
    <ProtectedRoute>
      {/*
       * `PartnerViewProvider` AYNAN shu yerda, `main.tsx` da emas.
       *
       * U `useNavigate()` ga tayanadi (kirganda bosh sahifaga, chiqqanda
       * ro'yxatga o'tkazadi), ya'ni `RouterProvider` ICHIDA bo'lishi shart —
       * `main.tsx` esa undan tashqarida. Bundan tashqari `ProtectedRoute`
       * ichida turgani uchun u faqat tizimga kirgan foydalanuvchi uchun
       * mavjud: login ekranida partner ko'rinishining ma'nosi yo'q.
       */}
      <PartnerViewProvider>
        <AppShell />
      </PartnerViewProvider>
    </ProtectedRoute>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardPage,
});

// ---------------------------------------------------------------------------
// Ro'yxat ekranlarining URL holati
// ---------------------------------------------------------------------------

/*
 * `validateSearch: parseListSearch` — URL ISHONCHSIZ kirish. Foydalanuvchi uni
 * qo'lda tahrirlashi mumkin, shuning uchun har maydon tozalanadi: `?page=-5`
 * yoki `?sortDir=DROP TABLE` ekranni buzmaydi, default'ga tushadi.
 *
 * Tozalash va default'lar `lib/list-search.ts` da — ular ekran komponentlariga
 * ham kerak, va ikki nusxa bo'lsa ular ertami-kechmi bir-biridan farq qiladi.
 */

// ---------------------------------------------------------------------------
// Mahsulotlar
// ---------------------------------------------------------------------------

const productsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products',
  /*
   * Umumiy `parseListSearch` EMAS: katalogda kategoriya va brend filtri ham
   * manzilda yashaydi (`?categoryId=&brandId=`) va ular ham tozalanishi
   * kerak — `parseProductSearch` o'sha ikkitasini qo'shadi.
   */
  validateSearch: parseProductSearch,
  component: lazyPage(ProductsListPage, ProductsRouteComponent),
});

function ProductsRouteComponent(): ReactNode {
  return <ProductsListPage search={productsRoute.useSearch()} />;
}

/*
 * `/reference` — kategoriya, brend, o'lchov birligi.
 *
 * `validateSearch` YO'Q: ekranda tab ham, arxiv bayrog'i ham LOKAL holat.
 * Ular manzilga chiqarilmadi, chunki bu ekranga havola ulashilmaydi —
 * u ish oqimining oxiri emas, boshi (ma'lumotnomani to'ldirish).
 */
const referenceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reference',
  component: ReferenceAdminPage,
});

/**
 * `/products/new` `/products/$id` DAN OLDIN e'lon qilingan.
 *
 * TanStack Router statik segmentni dinamikdan ustun qo'yadi, ya'ni tartib
 * texnik jihatdan majburiy emas. Lekin u O'QILADIGAN tartib: aynan shu
 * to'qnashuv API tomonda ham bor (`/products/search` va `:id`,
 * products.controller.ts) va u yerda tartib HAL QILUVCHI. Ikki qatlamda bir xil
 * ko'rinish qolgani ma'qul.
 */
const productNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products/new',
  component: ProductNewPage,
});

const productDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products/$id',
  component: lazyPage(ProductDetailPage, ProductDetailRouteComponent),
});

function ProductDetailRouteComponent(): ReactNode {
  const { id } = productDetailRoute.useParams();
  return <ProductDetailPage id={Number(id)} />;
}

// ---------------------------------------------------------------------------
// Mijozlar
// ---------------------------------------------------------------------------

const customersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers',
  validateSearch: parseListSearch,
  component: lazyPage(CustomersListPage, CustomersRouteComponent),
});

/*
 * `/customers/new` `/customers/$id` DAN OLDIN e'lon qilinadi — mahsulotdagi
 * bilan bir xil sabab: aks holda `new` `:id` ga tushib, `ParseIntPipe`
 * 400 qaytarardi.
 */
const customerNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers/new',
  component: CustomerNewPage,
});

function CustomersRouteComponent(): ReactNode {
  return <CustomersListPage search={customersRoute.useSearch()} />;
}

const customerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers/$id',
  component: lazyPage(CustomerDetailPage, CustomerDetailRouteComponent),
});

function CustomerDetailRouteComponent(): ReactNode {
  const { id } = customerDetailRoute.useParams();
  return <CustomerDetailPage id={Number(id)} />;
}

// ---------------------------------------------------------------------------
// Ta'minotchilar
// ---------------------------------------------------------------------------

/**
 * Ro'yxat va kartochka BITTA manzilda (`?id=`).
 *
 * `/suppliers/$id` alohida marshrut qilinmadi: ta'minotchilar soni o'nlab va
 * foydalanuvchi ularni SOLISHTIRADI. Har safar to'liq sahifaga o'tish
 * filtrni va sahifani yo'qotardi (`lib/procurement-search.ts` izohi).
 */
const suppliersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/suppliers',
  validateSearch: parseSupplierSearch,
  component: lazyPage(SuppliersPage, SuppliersRouteComponent),
});

function SuppliersRouteComponent(): ReactNode {
  return <SuppliersPage search={suppliersRoute.useSearch()} />;
}

// ---------------------------------------------------------------------------
// Moliya — hisob-faktura, to'lov, qarzlar, kurs
// ---------------------------------------------------------------------------

/*
 * Manzillar `/finance/…` ostida EMAS va bu ongli: `/invoices`, `/payments`,
 * `/receivables`, `/payables` — bular kundalik ish ekranlari va ularning
 * manzili qisqa bo'lishi kerak (ular og'zaki ham aytiladi: «invoyslarni och»).
 * Fayllar esa `routes/finance/` da turadi — u KOD tashkiloti, MANZIL emas.
 *
 * `/reports/…` ostidagi hisobotlardan farqi shu: hisobot bir savolga javob
 * beradi va yopiladi, bular esa ish oqimi (`reorder-runs` bilan bir xil
 * mulohaza).
 */
const invoicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices',
  validateSearch: parseInvoiceSearch,
  component: lazyPage(InvoicesPage, InvoicesRouteComponent),
});

function InvoicesRouteComponent(): ReactNode {
  return <InvoicesPage search={invoicesRoute.useSearch()} />;
}

const paymentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payments',
  validateSearch: parsePaymentSearch,
  component: lazyPage(PaymentsPage, PaymentsRouteComponent),
});

function PaymentsRouteComponent(): ReactNode {
  return <PaymentsPage search={paymentsRoute.useSearch()} />;
}

const receivablesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/receivables',
  validateSearch: parseReceivablesSearch,
  component: lazyPage(ReceivablesPage, ReceivablesRouteComponent),
});

function ReceivablesRouteComponent(): ReactNode {
  return <ReceivablesPage search={receivablesRoute.useSearch()} />;
}

const payablesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payables',
  validateSearch: parsePayablesSearch,
  component: lazyPage(PayablesPage, PayablesRouteComponent),
});

function PayablesRouteComponent(): ReactNode {
  return <PayablesPage search={payablesRoute.useSearch()} />;
}

/*
 * Manzil `/finance/...` — `/payments` kabi qisqa EMAS, va bu ataylab.
 *
 * `/payments` mijoz to'lovi va u shu nomda og'zaki ham aytiladi
 * («to'lovlarni och»). «Ta'minotchiga to'lov» esa hech qachon shunchaki
 * «to'lov» deb aytilmaydi — u har doim zavod bilan birga aytiladi. Ildizda
 * `/supplier-payments` turgan bo'lsa, ikki ekran manzil bo'yicha qo'shni
 * bo'lib ko'rinardi, ular esa boshqa ruxsat va boshqa yo'nalishdagi pul.
 *
 * `day-closing` va `expenses` ham aynan shu sababdan `/finance/...` da.
 */
const supplierPaymentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/supplier-payments',
  validateSearch: parseSupplierPaymentSearch,
  component: lazyPage(SupplierPaymentsPage, SupplierPaymentsRouteComponent),
});

function SupplierPaymentsRouteComponent(): ReactNode {
  return <SupplierPaymentsPage search={supplierPaymentsRoute.useSearch()} />;
}

const fxRatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/fx-rates',
  validateSearch: parseFxRateSearch,
  component: lazyPage(FxRatesPage, FxRatesRouteComponent),
});

function FxRatesRouteComponent(): ReactNode {
  return <FxRatesPage search={fxRatesRoute.useSearch()} />;
}

/*
 * Manzil `/finance/...` — boshqa moliya ekranlaridagi kabi qisqa (`/payments`)
 * emas.
 *
 * Sabab: `/day-closing` va `/expenses` ildizda turgan bo'lsa, ular menyudagi
 * «Filial kassasi» guruhiga tegishli ekani manzildan ko'rinmasdi. Qisqa
 * manzillar tarixiy: ular ekran yozilgan paytda menyu tuzilmasi boshqacha
 * edi va ularni endi o'zgartirish saqlangan havolalarni uzardi.
 */
const dayClosingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/day-closing',
  component: DayClosingPage,
});

const expensesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/expenses',
  component: ExpensesPage,
});

/*
 * Manzil `/finance/...`, menyudagi o'rni esa «Buxgalteriya».
 *
 * Nomuvofiqlik ko'rinadi, lekin u ongli: ekran MOLIYA ishi (valyuta
 * pozitsiyasi), natijasi esa BUXGALTERIYA hujjati (provodka). Menyu bandi
 * ruxsat bo'yicha joylashadi (`fxreval.*` seed'da `ACCOUNTING_CODES` ichida),
 * manzil esa qo'shni ekranlar bilan bir oilada qoladi — `day-closing` ham
 * `/finance/...` da turib «Filial kassasi» guruhida.
 */
const fxRevaluationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/fx-revaluation',
  component: FxRevaluationPage,
});

// ---------------------------------------------------------------------------
// Ombor — kundalik ekranlar
// ---------------------------------------------------------------------------

/*
 * `validateSearch` bu yerda ayniqsa muhim: qoldiq va daftar ekranlari
 * bir-birini HAVOLA orqali chaqiradi (`/inventory/stock` satri →
 * `/inventory/movements?productId=…&warehouseId=…`). Parametrlar tozalanmasa,
 * qo'lda tahrirlangan manzil ekranni bo'sh yoki xato holatda ochardi.
 */

const stockRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/stock',
  validateSearch: parseStockSearch,
  component: lazyPage(StockPage, StockRouteComponent),
});

function StockRouteComponent(): ReactNode {
  return <StockPage search={stockRoute.useSearch()} />;
}

const availabilityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/availability',
  component: lazyPage(AvailabilityPage, AvailabilityRouteComponent),
});

/*
 * O'ram funksiya kerak: `lazyPage()` ikkinchi argument sifatida ARGUMENTSIZ
 * komponent kutadi, lazy komponent esa props qabul qiladi. Uni to'g'ridan-
 * to'g'ri berish `Expected 1 or more, but got 0` xatosini beradi.
 */
function AvailabilityRouteComponent(): ReactNode {
  return <AvailabilityPage />;
}

const movementsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/movements',
  validateSearch: parseMovementSearch,
  component: lazyPage(MovementsPage, MovementsRouteComponent),
});

function MovementsRouteComponent(): ReactNode {
  return <MovementsPage search={movementsRoute.useSearch()} />;
}

const receiptRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/receipt',
  component: ReceiptPage,
});

const issueRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/issue',
  component: IssuePage,
});

const transfersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/transfers',
  component: TransfersPage,
});

const countsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory/counts',
  component: CountsPage,
});

// ---------------------------------------------------------------------------
// Kiruvchi yuk (ta'minot ko'prigi)
// ---------------------------------------------------------------------------

/**
 * Ta'minotchidan kelgan yuk xabarnomalari.
 *
 * `validateSearch` BOR va u shart: ekranda uch narsa saqlanadi — qaysi holat
 * ko'rilyapti (kutilayotganlar yoki tarix), qaysi sahifada va qaysi
 * xabarnoma ochiq. Ombor xodimi «bu yukni qachon qabul qilgan edik?» degan
 * savolga havola bilan javob beradi va o'sha havola AYNAN o'sha kartochkani
 * ochishi kerak.
 *
 * `/inventory/…` ostiga qo'yilmadi: u yerdagi ekranlar BIZNING ichki ombor
 * amallarimiz (kirim, chiqim, ko'chirish), bu esa BOSHQA tashkilotdan
 * keladigan hujjat — u faqat qabul qilingandan KEYIN ombor daftariga
 * tushadi.
 */
const incomingSupplyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/supply/incoming',
  validateSearch: parsePartnerSupplySearch,
  component: lazyPage(IncomingSupplyPage, IncomingSupplyRouteComponent),
});

function IncomingSupplyRouteComponent(): ReactNode {
  return <IncomingSupplyPage search={incomingSupplyRoute.useSearch()} />;
}

// ---------------------------------------------------------------------------
// Savdo
// ---------------------------------------------------------------------------

const salesOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/orders',
  validateSearch: parseSalesOrderSearch,
  component: lazyPage(SalesOrdersListPage, SalesOrdersRouteComponent),
});

function SalesOrdersRouteComponent(): ReactNode {
  return <SalesOrdersListPage search={salesOrdersRoute.useSearch()} />;
}

/**
 * `/sales/orders/new` `/sales/orders/$id` DAN OLDIN e'lon qilingan.
 *
 * `products/new` bilan bir xil sabab: TanStack statik segmentni dinamikdan
 * ustun qo'yadi, ya'ni tartib texnik jihatdan majburiy emas — lekin u
 * O'QILADIGAN tartib va API tomonda (Nest) aynan shu to'qnashuv HAL QILUVCHI.
 */
const salesOrderNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/orders/new',
  component: SalesOrderNewPage,
});

const salesOrderDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/orders/$id',
  component: lazyPage(SalesOrderDetailPage, SalesOrderDetailRouteComponent),
});

function SalesOrderDetailRouteComponent(): ReactNode {
  const { id } = salesOrderDetailRoute.useParams();
  return <SalesOrderDetailPage id={Number(id)} />;
}

/*
 * Vozvrat — ro'yxat, karantin navbati va hujjat kartochkasi.
 *
 * Manzil `/sales/…` ostida: qaytarish SAVDO jarayonining teskari tomoni va
 * u mijoz bilan bog'liq (`sales_returns.customer_id`). Ombor ekranlariga
 * qo'yish noto'g'ri bo'lardi — omborchi zanjirning faqat bir bo'g'inini
 * (qabul) bajaradi, hujjatning o'zi esa mijoz hujjati.
 *
 * `/sales/returns/quarantine` `/sales/returns/$id` DAN OLDIN e'lon qilingan
 * (`/sales/orders/new` bilan bir xil sabab): TanStack statikni dinamikdan
 * ustun qo'yadi, lekin tartib O'QILADIGAN bo'lib qolsin.
 */
const returnsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/returns',
  validateSearch: parseReturnSearch,
  component: lazyPage(ReturnsListPage, ReturnsRouteComponent),
});

function ReturnsRouteComponent(): ReactNode {
  return <ReturnsListPage search={returnsRoute.useSearch()} />;
}

const returnsQuarantineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/returns/quarantine',
  validateSearch: parseQuarantineSearch,
  component: lazyPage(ReturnsQuarantinePage, ReturnsQuarantineRouteComponent),
});

function ReturnsQuarantineRouteComponent(): ReactNode {
  return <ReturnsQuarantinePage search={returnsQuarantineRoute.useSearch()} />;
}

const returnDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/returns/$id',
  component: lazyPage(ReturnDetailPage, ReturnDetailRouteComponent),
});

function ReturnDetailRouteComponent(): ReactNode {
  const { id } = returnDetailRoute.useParams();
  return <ReturnDetailPage id={Number(id)} />;
}

/**
 * Yetkazishlar va haydovchiga biriktirish.
 *
 * `/sales/orders/$id` DAN KEYIN e'lon qilingan, lekin `/sales/deliveries`
 * bilan to'qnashuv YO'Q: `$id` bitta segmentni oladi va `deliveries` statik
 * segment sifatida undan ustun turadi (TanStack qoidasi — `products/new`
 * bilan bir xil holat).
 */
const deliveriesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/deliveries',
  validateSearch: parseDeliverySearch,
  component: lazyPage(DeliveriesPage, DeliveriesRouteComponent),
});

/*
 * ODDIY SAVDO — `/sales/direct`.
 *
 * `validateSearch` YO'Q va bu ongli: kassa ekranida saqlanadigan holat yo'q.
 * Savdo tugagach hammasi tozalanadi va manzil almashmaydi — aks holda
 * brauzer «orqaga» tugmasi tugallangan savdoni qayta ochib, sotuvchini
 * chalg'itardi.
 */
const directSaleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/direct',
  component: DirectSalePage,
});

const myCashRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sales/my-cash',
  component: MyCashPage,
});

function DeliveriesRouteComponent(): ReactNode {
  return <DeliveriesPage search={deliveriesRoute.useSearch()} />;
}

// ---------------------------------------------------------------------------
// Hisobotlar (MVP_PLAN §2.8)
// ---------------------------------------------------------------------------

/*
 * Har hisobot O'Z manzilida. Bitta `/reports?type=…` ekran qilinmadi:
 *   • ruxsat har hisobotda boshqacha (`config/menu.ts`), bitta marshrutda
 *     ular chalkashib ketardi;
 *   • havola manzili («/reports/gross-profit») nima ochilishini o'zi aytadi;
 *   • `defaultPreload: 'intent'` faqat alohida marshrutlarda foyda beradi.
 *
 * `validateSearch` — URL ISHONCHSIZ kirish (`lib/report-search.ts` izohi).
 * Ekranlar hech qachon xom `search` ni ko'rmaydi.
 */

/*
 * `/reports` — hisobotlar markazi.
 *
 * Bola marshrutlardan (`/reports/stock` va h.k.) OLDIN e'lon qilingani
 * ahamiyatsiz: TanStack Router yo'lni aniqlik bo'yicha tanlaydi, e'lon
 * tartibi bo'yicha emas. Bu yerda turishining sababi faqat o'qilishi —
 * markaz o'zi to'plagan hisobotlar ro'yxatining boshida tursin.
 */
const reportsHubRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  component: ReportsHubPage,
});

const stockReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/stock',
  component: StockReportPage,
});

const stockValuationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/stock-valuation',
  component: StockValuationReportPage,
});

const salesReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/sales',
  validateSearch: parseSalesReportSearch,
  component: lazyPage(SalesReportPage, SalesReportRouteComponent),
});

function SalesReportRouteComponent(): ReactNode {
  return <SalesReportPage search={salesReportRoute.useSearch()} />;
}

/**
 * Agent faoliyati.
 *
 * `/reports/sales?groupBy=agent` ga QO'SHILMADI: o'sha kesim savdo hajmini
 * xodim bo'yicha bo'ladi, bu hisobot esa boshqa savolga javob beradi — «agent
 * qanday ishlayapti»: mijozlar qamrovi, dala buyurtmalari va tashrif joyi.
 * Ustunlar to'plami umuman boshqacha, ya'ni bitta ekranga sig'dirish ikkala
 * hisobotni ham o'qib bo'lmas qilardi.
 */
const agentActivityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/agent-activity',
  validateSearch: parseAgentActivitySearch,
  component: lazyPage(AgentActivityReportPage, AgentActivityRouteComponent),
});

function AgentActivityRouteComponent(): ReactNode {
  return <AgentActivityReportPage search={agentActivityRoute.useSearch()} />;
}

const grossProfitRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/gross-profit',
  validateSearch: parseProfitReportSearch,
  component: lazyPage(GrossProfitReportPage, GrossProfitRouteComponent),
});

function GrossProfitRouteComponent(): ReactNode {
  return <GrossProfitReportPage search={grossProfitRoute.useSearch()} />;
}

const arAgingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/ar-aging',
  validateSearch: parseAgingReportSearch,
  component: lazyPage(ArAgingReportPage, ArAgingRouteComponent),
});

const customerDisciplineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/customer-discipline',
  validateSearch: parseDisciplineReportSearch,
  component: lazyPage(CustomerDisciplineReportPage, CustomerDisciplineRouteComponent),
});

function CustomerDisciplineRouteComponent(): ReactNode {
  return <CustomerDisciplineReportPage search={customerDisciplineRoute.useSearch()} />;
}

function ArAgingRouteComponent(): ReactNode {
  return <ArAgingReportPage search={arAgingRoute.useSearch()} />;
}

const cashFlowRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/cash-flow',
  validateSearch: parsePeriodSearch,
  component: lazyPage(CashFlowReportPage, CashFlowRouteComponent),
});

function CashFlowRouteComponent(): ReactNode {
  return <CashFlowReportPage search={cashFlowRoute.useSearch()} />;
}

const deadStockRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/dead-stock',
  validateSearch: parseDeadStockReportSearch,
  component: lazyPage(DeadStockReportPage, DeadStockRouteComponent),
});

function DeadStockRouteComponent(): ReactNode {
  return <DeadStockReportPage search={deadStockRoute.useSearch()} />;
}

const shipmentProfitRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/shipment-profitability',
  validateSearch: parseShipmentReportSearch,
  component: lazyPage(ShipmentProfitabilityPage, ShipmentProfitRouteComponent),
});

function ShipmentProfitRouteComponent(): ReactNode {
  return <ShipmentProfitabilityPage search={shipmentProfitRoute.useSearch()} />;
}

/**
 * Qachon buyurtma berish kerak (GAP_ANALYSIS §A4).
 *
 * `validateSearch` YO'Q va bu ongli: uchta parametr (talab davri, xavfsizlik
 * zaxirasi, standart lead time) — hisoblash FARAZI, «qaysi ma'lumot» emas.
 * Havolada ulashishga arzigulik narsa XULOSA («shu SKU larni buyurtma
 * qiling»), u esa default parametrlarda ham bir xil chiqadi.
 */
const reorderRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/reorder',
  component: ReorderReportPage,
});

/**
 * Drill-down: konteyner → SKU tannarx tarkibi.
 *
 * Menyuda YO'Q va bo'lishi ham mumkin emas — u konkret yukni talab qiladi.
 * Kirish nuqtasi bitta: konteyner rentabelligi jadvalidagi satr.
 */
const landedCostRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/landed-cost/$shipmentId',
  component: lazyPage(LandedCostReportPage, LandedCostRouteComponent),
});

function LandedCostRouteComponent(): ReactNode {
  const { shipmentId } = landedCostRoute.useParams();
  return <LandedCostReportPage shipmentId={Number(shipmentId)} />;
}

// ---------------------------------------------------------------------------
// Tahlil hisobotlari (TZ v4.0 §28 / CR-007)
// ---------------------------------------------------------------------------

/*
 * Har biri O'Z manzilida — yuqoridagi bir xil sabab. `validateSearch` esa
 * hisobotning MA'NOSINI o'zgartiradigan parametrlar uchun:
 *
 *   ABC        — davr VA chegara (chegara guruhni o'zgartiradi);
 *   XYZ        — kuzatuv oynasi va davr turi (barqarorlik ularsiz o'lchanmaydi);
 *   RFM / foyda — davr;
 *   LTV        — eng qisqa tarix chegarasi (qaysi satrga oylik qiymat beriladi);
 *   ta'minotchi / konteyner — kuzatuv oynasi;
 *   ombor yoshi — ixtiyoriy ombor.
 *
 * `reorder` esa `validateSearch` SIZ qolgan va bu farq ongli: u yerdagi
 * parametrlar hisoblash farazi, bu yerdagilar esa TASNIF chegarasi.
 */

const abcReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/abc',
  validateSearch: parseAbcReportSearch,
  component: lazyPage(AbcReportPage, AbcRouteComponent),
});

function AbcRouteComponent(): ReactNode {
  return <AbcReportPage search={abcReportRoute.useSearch()} />;
}

const xyzReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/xyz',
  validateSearch: parseXyzReportSearch,
  component: lazyPage(XyzReportPage, XyzRouteComponent),
});

function XyzRouteComponent(): ReactNode {
  return <XyzReportPage search={xyzReportRoute.useSearch()} />;
}

const rfmReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/rfm',
  validateSearch: parsePeriodSearch,
  component: lazyPage(RfmReportPage, RfmRouteComponent),
});

function RfmRouteComponent(): ReactNode {
  return <RfmReportPage search={rfmReportRoute.useSearch()} />;
}

const ltvReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/ltv',
  validateSearch: parseLtvReportSearch,
  component: lazyPage(LtvReportPage, LtvRouteComponent),
});

function LtvRouteComponent(): ReactNode {
  return <LtvReportPage search={ltvReportRoute.useSearch()} />;
}

const supplierScorecardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/supplier-scorecard',
  validateSearch: parseSupplierScorecardSearch,
  component: lazyPage(SupplierScorecardPage, SupplierScorecardRouteComponent),
});

function SupplierScorecardRouteComponent(): ReactNode {
  return <SupplierScorecardPage search={supplierScorecardRoute.useSearch()} />;
}

const containerEfficiencyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/container-efficiency',
  validateSearch: parseContainerReportSearch,
  component: lazyPage(ContainerEfficiencyPage, ContainerEfficiencyRouteComponent),
});

function ContainerEfficiencyRouteComponent(): ReactNode {
  return <ContainerEfficiencyPage search={containerEfficiencyRoute.useSearch()} />;
}

const stockAgeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/stock-age',
  validateSearch: parseStockAgeReportSearch,
  component: lazyPage(StockAgeReportPage, StockAgeRouteComponent),
});

function StockAgeRouteComponent(): ReactNode {
  return <StockAgeReportPage search={stockAgeRoute.useSearch()} />;
}

const customerProfitabilityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/customer-profitability',
  validateSearch: parsePeriodSearch,
  component: lazyPage(CustomerProfitabilityPage, CustomerProfitabilityRouteComponent),
});

function CustomerProfitabilityRouteComponent(): ReactNode {
  return <CustomerProfitabilityPage search={customerProfitabilityRoute.useSearch()} />;
}

// ---------------------------------------------------------------------------
// Buxgalteriya (TZ v8.0 §34, §55)
// ---------------------------------------------------------------------------

/*
 * Uch moliyaviy hisobot davrni (yoki sanani) URL da saqlaydi — qolgan
 * hisobotlar bilan bir xil sabab: «avgust balansi» ekrani havola qilib
 * yuboriladi va u AYNAN o'sha holatni ochadi.
 *
 * P&L va kapital `parsePeriodSearch` dan foydalanadi (ikkalasi ham davr),
 * balans esa BITTA sana oladi — u davr emas, KESIM.
 */

const incomeStatementRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/income-statement',
  validateSearch: parsePeriodSearch,
  component: lazyPage(IncomeStatementPage, IncomeStatementRouteComponent),
});

function IncomeStatementRouteComponent(): ReactNode {
  return <IncomeStatementPage search={incomeStatementRoute.useSearch()} />;
}

const balanceSheetRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/balance-sheet',
  validateSearch: parseBalanceSheetSearch,
  component: lazyPage(BalanceSheetPage, BalanceSheetRouteComponent),
});

function BalanceSheetRouteComponent(): ReactNode {
  return <BalanceSheetPage search={balanceSheetRoute.useSearch()} />;
}

const equityReportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/equity',
  validateSearch: parsePeriodSearch,
  component: lazyPage(EquityReportPage, EquityReportRouteComponent),
});

function EquityReportRouteComponent(): ReactNode {
  return <EquityReportPage search={equityReportRoute.useSearch()} />;
}

const settingsAccountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/accounts',
  component: AccountsSettingsPage,
});

const accountingPeriodsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/accounting/periods',
  validateSearch: parsePeriodsSearch,
  component: lazyPage(AccountingPeriodsPage, AccountingPeriodsRouteComponent),
});

function AccountingPeriodsRouteComponent(): ReactNode {
  return <AccountingPeriodsPage search={accountingPeriodsRoute.useSearch()} />;
}

const accountingJournalsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/accounting/journals',
  validateSearch: parseJournalsSearch,
  component: lazyPage(AccountingJournalsPage, AccountingJournalsRouteComponent),
});

function AccountingJournalsRouteComponent(): ReactNode {
  return <AccountingJournalsPage search={accountingJournalsRoute.useSearch()} />;
}

/**
 * Provodka kartochkasi — menyuda YO'Q va bo'lishi ham mumkin emas.
 *
 * U konkret provodkani talab qiladi (`/reports/landed-cost/$shipmentId`
 * bilan bir xil sabab). Kirish nuqtalari ikkita: provodkalar ro'yxatidagi
 * satr va hisobot satridan boshlanadigan zanjir.
 */
const journalDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/accounting/journals/$id',
  component: lazyPage(JournalDetailPage, JournalDetailRouteComponent),
});

function JournalDetailRouteComponent(): ReactNode {
  const { id } = journalDetailRoute.useParams();
  return <JournalDetailPage id={Number(id)} />;
}

// ---------------------------------------------------------------------------
// Xarid va import (LANDED_COST_SPEC, GAP_ANALYSIS §A1/§A3)
// ---------------------------------------------------------------------------

/**
 * Ta'minot bo'limining ish maydoni.
 *
 * `validateSearch` YO'Q va bu ongli: ekranda filtr ham, sahifalash ham yo'q —
 * u BUGUNGI holatni ko'rsatadi va uning yagona parametri bugungi sana.
 * «3-sahifa, faqat kechikkanlar» degan havolani hech kim ulashmaydi
 * (`supplier-invoices` route'idagi bir xil mulohaza).
 */
const procurementWorkspaceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/workspace',
  component: ProcurementWorkspacePage,
});

/*
 * `/procurement/orders/new` `/procurement/orders/$id` DAN OLDIN e'lon
 * qilingan — mahsulot va sotuv buyurtmalari bilan bir xil sabab: TanStack
 * statik segmentni dinamikdan ustun qo'yadi, lekin O'QILADIGAN tartib muhim
 * va API tomonda (Nest) aynan shu to'qnashuv hal qiluvchi.
 */

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/orders',
  validateSearch: parsePurchaseOrderSearch,
  component: lazyPage(PurchaseOrdersListPage, PurchaseOrdersRouteComponent),
});

function PurchaseOrdersRouteComponent(): ReactNode {
  return <PurchaseOrdersListPage search={purchaseOrdersRoute.useSearch()} />;
}

const purchaseOrderNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/orders/new',
  component: PurchaseOrderNewPage,
});

const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/orders/$id',
  component: lazyPage(PurchaseOrderDetailPage, PurchaseOrderDetailRouteComponent),
});

function PurchaseOrderDetailRouteComponent(): ReactNode {
  const { id } = purchaseOrderDetailRoute.useParams();
  return <PurchaseOrderDetailPage id={Number(id)} />;
}

/*
 * Avtomatik qayta zakaz — ro'yxat va kartochka.
 *
 * Manzil `/procurement/…` ostida: bu hisobot emas, XARID ish oqimi va uning
 * natijasi qoralama xarid buyurtmasi bo'lib chiqadi (ekran sarlavhasidagi
 * izoh). `/reports/reorder` esa o'z joyida qoladi — u bir savolga javob
 * beradi va hech narsa yozmaydi.
 */
const reorderRunsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/reorder-runs',
  validateSearch: parseReorderRunSearch,
  component: lazyPage(ReorderRunsPage, ReorderRunsRouteComponent),
});

function ReorderRunsRouteComponent(): ReactNode {
  return <ReorderRunsPage search={reorderRunsRoute.useSearch()} />;
}

const reorderRunDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/reorder-runs/$id',
  component: lazyPage(ReorderRunDetailPage, ReorderRunDetailRouteComponent),
});

function ReorderRunDetailRouteComponent(): ReactNode {
  const { id } = reorderRunDetailRoute.useParams();
  return <ReorderRunDetailPage id={Number(id)} />;
}

/*
 * Komissiya va KPI.
 *
 * Manzil `/hr/…` ostida, `/reports/…` da EMAS: bu yerdagi raqam saqlanadi,
 * ko'rib chiqiladi va YOPILADI — ya'ni ish oqimi va to'lov majburiyati.
 * Hisobot esa savolga javob beradi va hech narsa yozmaydi
 * (`reorder-runs` bilan aynan bir xil mulohaza).
 */
const commissionRunsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hr/commission',
  validateSearch: parseCommissionRunSearch,
  component: lazyPage(CommissionRunsPage, CommissionRunsRouteComponent),
});

function CommissionRunsRouteComponent(): ReactNode {
  return <CommissionRunsPage search={commissionRunsRoute.useSearch()} />;
}

const commissionRunDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hr/commission/$id',
  component: lazyPage(CommissionRunDetailPage, CommissionRunDetailRouteComponent),
});

function CommissionRunDetailRouteComponent(): ReactNode {
  const { id } = commissionRunDetailRoute.useParams();
  return <CommissionRunDetailPage id={Number(id)} />;
}

const employeesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hr/employees',
  validateSearch: parseEmployeeSearch,
  component: lazyPage(EmployeesPage, EmployeesRouteComponent),
});

function EmployeesRouteComponent(): ReactNode {
  return <EmployeesPage search={employeesRoute.useSearch()} />;
}

const dealerControlRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dealers/control',
  component: DealerControlPage,
});

const mySettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/me',
  component: MySettingsPage,
});

const treasuryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/treasury',
  component: TreasuryPage,
});

const creditNotesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/credit-notes',
  validateSearch: parseCreditNoteSearch,
  component: lazyPage(CreditNotesPage, CreditNotesRouteComponent),
});

function CreditNotesRouteComponent(): ReactNode {
  return <CreditNotesPage search={creditNotesRoute.useSearch()} />;
}

const discountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/discounts',
  component: DiscountsPage,
});

const hrControlRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hr/control',
  component: ControlPage,
});

const payrollRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hr/payroll',
  component: PayrollPage,
});

const charityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/finance/charity',
  component: CharityPage,
});

const kpiRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hr/kpi',
  validateSearch: parseKpiSearch,
  component: lazyPage(KpiPage, KpiRouteComponent),
});

function KpiRouteComponent(): ReactNode {
  return <KpiPage search={kpiRoute.useSearch()} />;
}

const shipmentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/shipments',
  validateSearch: parseShipmentSearch,
  component: lazyPage(ShipmentsListPage, ShipmentsRouteComponent),
});

function ShipmentsRouteComponent(): ReactNode {
  return <ShipmentsListPage search={shipmentsRoute.useSearch()} />;
}

const shipmentDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/shipments/$id',
  component: lazyPage(ShipmentDetailPage, ShipmentDetailRouteComponent),
});

function ShipmentDetailRouteComponent(): ReactNode {
  const { id } = shipmentDetailRoute.useParams();
  return <ShipmentDetailPage id={Number(id)} />;
}

/**
 * Tannarx bo'limining ish maydoni.
 *
 * `validateSearch` YO'Q va bu ongli: tanlangan konteyner URL ga chiqmaydi.
 * Sabab — bu ekran BIR ODAMNING ish stoli, ulashiladigan hisobot emas:
 * bo'lim konteynerni ochadi, xarajatlarni kiritadi va yopadi. «Bu konteynerni
 * ko'r» degan havola kerak bo'lganda `/procurement/shipments/$id` bor va
 * unda tannarx tabi ham mavjud.
 */
const costingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/costing',
  component: CostingPage,
});

const goodsReceiptsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/receipts',
  validateSearch: parseGoodsReceiptSearch,
  component: lazyPage(GoodsReceiptsPage, GoodsReceiptsRouteComponent),
});

function GoodsReceiptsRouteComponent(): ReactNode {
  return <GoodsReceiptsPage search={goodsReceiptsRoute.useSearch()} />;
}

/**
 * Ta'minotchi invoyslari.
 *
 * `validateSearch` YO'Q va bu ongli: ekranning holati (sahifa, holat filtri)
 * URL ga chiqarilmaydi. Sabab — bu REYESTR emas, KIRITISH ekrani: xodim unga
 * hujjat qo'lida kelib, kiritib chiqib ketadi. «3-sahifa, faqat qoralamalar»
 * degan havolani hech kim ulashmaydi.
 *
 * Boshqa ro'yxatlarda (mahsulot, mijoz, hisob-faktura) URL holati BOR va u
 * o'sha yerda zarur: kartochkaga kirib chiqilgach ro'yxat o'z joyiga
 * qaytishi kerak. Bu yerda kartochka yo'q.
 */
const supplierInvoicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/procurement/supplier-invoices',
  component: SupplierInvoicesPage,
});

/**
 * Import ustasi.
 *
 * `batchId` URL da turishi MAJBURIY: usta 4 bosqichli va operator o'rtada
 * sahifani yangilashi mumkin. Partiya ID si yo'qolsa, yuklangan fayl bazada
 * qolib ketadi va uni ekrandan davom ettirib bo'lmaydi.
 */
const importRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/import',
  validateSearch: parseImportSearch,
  component: lazyPage(ImportWizardPage, ImportRouteComponent),
});

function ImportRouteComponent(): ReactNode {
  return <ImportWizardPage search={importRoute.useSearch()} />;
}

// ---------------------------------------------------------------------------
// Sozlamalar va administratorlik (PERMISSION_MATRIX §3.7)
// ---------------------------------------------------------------------------

const settingsUsersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/users',
  component: UsersSettingsPage,
});

const settingsRolesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/roles',
  component: RolesSettingsPage,
});

const settingsPriceListsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/price-lists',
  validateSearch: parsePriceListSearch,
  component: lazyPage(PriceListsSettingsPage, SettingsPriceListsRouteComponent),
});

function SettingsPriceListsRouteComponent(): ReactNode {
  return <PriceListsSettingsPage search={settingsPriceListsRoute.useSearch()} />;
}

const settingsCashAccountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/cash-accounts',
  component: CashAccountsSettingsPage,
});

const settingsCashFlowItemsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/cash-flow-items',
  component: CashFlowItemsSettingsPage,
});

const settingsOrganizationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/organizations',
  component: OrganizationsSettingsPage,
});

const settingsOrganizationModulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/organizations/$id/modules',
  component: lazyPage(OrganizationModulesPage, SettingsOrganizationModulesRouteComponent),
});

function SettingsOrganizationModulesRouteComponent(): ReactNode {
  const { id } = settingsOrganizationModulesRoute.useParams();
  return <OrganizationModulesPage id={Number(id)} />;
}

const aiAskRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/ai',
  component: AiAskPage,
});

const settingsPrintTemplatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/print-templates',
  component: PrintTemplatesSettingsPage,
});

const trialBalanceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/accounting/trial-balance',
  component: TrialBalancePage,
});

const reconciliationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/accounting/reconciliation',
  component: ReconciliationPage,
});

/**
 * Muddati tugayotgan hujjatlar.
 *
 * `validateSearch` YO'Q: oyna (7/30/90 kun) faqat shu ekrandagi ko'rish
 * sozlamasi va uni URL ga chiqarish hech narsa bermaydi — bu ro'yxatga
 * havola tashlanmaydi, u har kuni ochib ko'riladigan ekran.
 */
const documentsExpiringRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/documents/expiring',
  component: ExpiringDocumentsPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/system',
  component: SystemSettingsPage,
});

/**
 * Tashkiliy tuzilma.
 *
 * `validateSearch` YO'Q: ekranda filtr ham, sahifalash ham yo'q — filiallar
 * soni o'nlab bo'lmaydi va daraxt bir ekranga sig'adi. URL ga chiqariladigan
 * holat bo'lmasa, uni tekshirish ham kerak emas.
 */
const settingsOrganizationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/organization',
  component: OrganizationSettingsPage,
});

/*
 * Tasdiqlash navbati — SOZLAMALARDA emas, o'z manzilida (`/approvals`).
 *
 * Sozlamalar ekranlari haftada bir marta ochiladi, navbat esa KUNIGA bir
 * necha marta: uni sozlamalar ostiga yashirish har safar ikki bosishga
 * aylantirardi. Qoidalar va vakolat esa aynan sozlama va ular
 * `/settings/*` da qoladi.
 */
const approvalsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/approvals',
  component: ApprovalsPage,
});

const settingsApprovalRulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/approval-rules',
  component: ApprovalRulesSettingsPage,
});

const settingsDelegationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/delegations',
  component: DelegationsSettingsPage,
});

/*
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ XAVFSIZLIK — O'Z MANZILIDA (`/security`), SOZLAMALAR ICHIDA EMAS         ║
 * ║                                                                          ║
 * ║ Ekran `routes/settings/audit.tsx` da edi va menyuda «Tizim» guruhining   ║
 * ║ uchinchi bandi bo'lib turardi. Endi u alohida bo'lim (`config/menu.ts`   ║
 * ║ dagi «Xavfsizlik» ramkasi).                                              ║
 * ║                                                                          ║
 * ║ IKKI NUSXA QOLDIRILMADI: eski fayl O'CHIRILDI, ekran BITTA. Bu shart —   ║
 * ║ ikkita nusxaning biri albatta eskiradi va ular ayni bir ma'lumotni       ║
 * ║ boshqacha ko'rsata boshlaydi.                                            ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
const securityLogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/security',
  validateSearch: parseAuditSearch,
  component: lazyPage(SecurityLogPage, SecurityLogRouteComponent),
});

function SecurityLogRouteComponent(): ReactNode {
  return <SecurityLogPage search={securityLogRoute.useSearch()} />;
}

/*
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ ESKI MANZIL — YO'NALTIRUVCHI, O'CHIRILGAN EMAS                          ║
 * ║                                                                          ║
 * ║ `/settings/audit` bir necha oy tirik ishlagan: u xatcho'plarga tushgan,  ║
 * ║ va ekranlar ichidan ham havola qilingan (masalan kurs kartochkasidagi    ║
 * ║ «tarixni ko'rish» — u `?entityTable=daily_rates` bilan ochadi).          ║
 * ║ Ichki havolalar yangi manzilga o'tkazildi, xatcho'plar esa o'tkazib      ║
 * ║ bo'lmaydigan narsa.                                                      ║
 * ║                                                                          ║
 * ║ Manzilni O'CHIRISH varianti RAD ETILDI: eski havola 404 berardi va bu    ║
 * ║ eng noqulay joyda chiqardi — odam nimadir tekshirmoqchi bo'lganda.       ║
 * ║ EKRANNI IKKI MANZILDA ko'rsatish ham RAD ETILDI: ikki nusxa bir kun      ║
 * ║ bir-biridan farq qila boshlaydi.                                         ║
 * ║                                                                          ║
 * ║ Yo'naltiruvchida EKRAN YO'Q — faqat `beforeLoad`, ya'ni komponent        ║
 * ║ umuman yuklanmaydi. Qidiruv parametrlari SAQLANADI: eski havoladagi      ║
 * ║ `?entityTable=daily_rates` yo'qolsa, odam butun jurnalga tushib          ║
 * ║ qolardi va nima izlayotganini qaytadan yozardi.                          ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
const settingsAuditRedirectRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/audit',
  validateSearch: parseAuditSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/security', search, replace: true });
  },
});

// ---------------------------------------------------------------------------
// Usta dasturi (loyalty) — LOYALTY_SPEC §6 «Admin: ERP ichida»
// ---------------------------------------------------------------------------

/*
 * Usta O'ZI bu marshrutlarga kirmaydi: uning interfeysi alohida ilova
 * (`apps/usta`) va uning tokeni ERP guard'ini qanoatlantirmaydi. Bu yerdagi
 * ekranlar OFIS uchun — qoida, kod, katalog, tasdiqlash va nazorat.
 *
 * `validateSearch` boshqa ro'yxat ekranlari bilan bir xil sababdan
 * (`lib/loyalty-search.ts`): URL ishonchsiz kirish. Ayniqsa
 * `qr-batches?exportId=` muhim — u eksportning TASDIQLASH qadamini ochadi,
 * eksportning o'zini BAJARMAYDI.
 */

const loyaltyStatusRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty',
  component: LoyaltyStatusPage,
});

const loyaltyPointRulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/point-rules',
  validateSearch: parsePointRuleSearch,
  component: lazyPage(LoyaltyPointRulesPage, LoyaltyPointRulesRouteComponent),
});

function LoyaltyPointRulesRouteComponent(): ReactNode {
  return <LoyaltyPointRulesPage search={loyaltyPointRulesRoute.useSearch()} />;
}

const loyaltyQrBatchesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/qr-batches',
  validateSearch: parseQrBatchSearch,
  component: lazyPage(LoyaltyQrBatchesPage, LoyaltyQrBatchesRouteComponent),
});

function LoyaltyQrBatchesRouteComponent(): ReactNode {
  return <LoyaltyQrBatchesPage search={loyaltyQrBatchesRoute.useSearch()} />;
}

const loyaltyRewardsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/rewards',
  component: LoyaltyRewardsPage,
});

const loyaltyRedemptionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/redemptions',
  validateSearch: parseRedemptionSearch,
  component: lazyPage(LoyaltyRedemptionsPage, LoyaltyRedemptionsRouteComponent),
});

function LoyaltyRedemptionsRouteComponent(): ReactNode {
  return <LoyaltyRedemptionsPage search={loyaltyRedemptionsRoute.useSearch()} />;
}

const loyaltyInstallersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/installers',
  validateSearch: parseInstallerSearch,
  component: lazyPage(LoyaltyInstallersPage, LoyaltyInstallersRouteComponent),
});

function LoyaltyInstallersRouteComponent(): ReactNode {
  return <LoyaltyInstallersPage search={loyaltyInstallersRoute.useSearch()} />;
}

/**
 * Usta kartochkasi — ball daftari va rekonsiliatsiya.
 *
 * Menyuda YO'Q va bo'lishi ham mumkin emas: u konkret ustani talab qiladi
 * (`/reports/landed-cost/$shipmentId` bilan bir xil sabab). Kirish nuqtasi —
 * ro'yxatdagi satr.
 */
const loyaltyInstallerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/installers/$id',
  component: lazyPage(LoyaltyInstallerDetailPage, LoyaltyInstallerDetailRouteComponent),
});

function LoyaltyInstallerDetailRouteComponent(): ReactNode {
  const { id } = loyaltyInstallerDetailRoute.useParams();
  return <LoyaltyInstallerDetailPage id={Number(id)} />;
}

const loyaltyFraudRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/loyalty/fraud',
  validateSearch: parseFraudSearch,
  component: lazyPage(LoyaltyFraudPage, LoyaltyFraudRouteComponent),
});

function LoyaltyFraudRouteComponent(): ReactNode {
  return <LoyaltyFraudPage search={loyaltyFraudRoute.useSearch()} />;
}

// ---------------------------------------------------------------------------
// Diller kanali va bonus — TZ v8.0 §31
// ---------------------------------------------------------------------------

/*
 * `/bonus/entries` `/bonus` DAN KEYIN e'lon qilinadi, lekin ikkalasi ham
 * to'liq (aniq) yo'l — TanStack Router ularni bir-biriga aralashtirmaydi.
 * Tartib faqat o'qish uchun: avval «kimga qancha», keyin «nima bo'lgan».
 */
const dealersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dealers',
  validateSearch: parseDealerSearch,
  component: lazyPage(DealersPage, DealersRouteComponent),
});

function DealersRouteComponent(): ReactNode {
  return <DealersPage search={dealersRoute.useSearch()} />;
}

const bonusAccountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bonus',
  validateSearch: parseBonusSearch,
  component: lazyPage(BonusAccountsPage, BonusAccountsRouteComponent),
});

function BonusAccountsRouteComponent(): ReactNode {
  return <BonusAccountsPage search={bonusAccountsRoute.useSearch()} />;
}

const bonusLedgerRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bonus/entries',
  validateSearch: parseBonusLedgerSearch,
  component: lazyPage(BonusLedgerPage, BonusLedgerRouteComponent),
});

function BonusLedgerRouteComponent(): ReactNode {
  return <BonusLedgerPage search={bonusLedgerRoute.useSearch()} />;
}

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    productsRoute,
    productNewRoute,
    productDetailRoute,
    referenceRoute,
    customersRoute,
    customerNewRoute,
    customerDetailRoute,
    suppliersRoute,
    invoicesRoute,
    paymentsRoute,
    receivablesRoute,
    payablesRoute,
    supplierPaymentsRoute,
    fxRatesRoute,
    dayClosingRoute,
    expensesRoute,
    fxRevaluationRoute,
    stockRoute,
    availabilityRoute,
    movementsRoute,
    receiptRoute,
    issueRoute,
    transfersRoute,
    countsRoute,
    incomingSupplyRoute,
    directSaleRoute,
    myCashRoute,
    salesOrdersRoute,
    salesOrderNewRoute,
    salesOrderDetailRoute,
    returnsRoute,
    returnsQuarantineRoute,
    returnDetailRoute,
    deliveriesRoute,
    procurementWorkspaceRoute,
    purchaseOrdersRoute,
    purchaseOrderNewRoute,
    purchaseOrderDetailRoute,
    reorderRunsRoute,
    reorderRunDetailRoute,
    commissionRunsRoute,
    commissionRunDetailRoute,
    employeesRoute,
    payrollRoute,
    hrControlRoute,
    discountsRoute,
    creditNotesRoute,
    treasuryRoute,
    mySettingsRoute,
    dealerControlRoute,
    charityRoute,
    kpiRoute,
    shipmentsRoute,
    shipmentDetailRoute,
    costingRoute,
    goodsReceiptsRoute,
    supplierInvoicesRoute,
    importRoute,
    settingsUsersRoute,
    settingsRolesRoute,
    settingsPriceListsRoute,
    settingsCashAccountsRoute,
    settingsCashFlowItemsRoute,
    settingsOrganizationsRoute,
    settingsOrganizationModulesRoute,
    aiAskRoute,
    settingsPrintTemplatesRoute,
    trialBalanceRoute,
    reconciliationRoute,
    settingsSystemRoute,
    settingsOrganizationRoute,
    securityLogRoute,
    settingsAuditRedirectRoute,
    approvalsRoute,
    settingsApprovalRulesRoute,
    settingsDelegationsRoute,
    reportsHubRoute,
    stockReportRoute,
    stockValuationRoute,
    salesReportRoute,
    agentActivityRoute,
    grossProfitRoute,
    arAgingRoute,
    customerDisciplineRoute,
    cashFlowRoute,
    deadStockRoute,
    shipmentProfitRoute,
    reorderRoute,
    landedCostRoute,
    abcReportRoute,
    xyzReportRoute,
    rfmReportRoute,
    ltvReportRoute,
    supplierScorecardRoute,
    containerEfficiencyRoute,
    stockAgeRoute,
    customerProfitabilityRoute,
    incomeStatementRoute,
    balanceSheetRoute,
    equityReportRoute,
    settingsAccountsRoute,
    accountingPeriodsRoute,
    accountingJournalsRoute,
    journalDetailRoute,
    loyaltyStatusRoute,
    loyaltyPointRulesRoute,
    loyaltyQrBatchesRoute,
    loyaltyRewardsRoute,
    loyaltyRedemptionsRoute,
    loyaltyInstallersRoute,
    loyaltyInstallerDetailRoute,
    loyaltyFraudRoute,
    dealersRoute,
    bonusAccountsRoute,
    bonusLedgerRoute,
    documentsExpiringRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
  // Havola ustiga kelganda oldindan yuklash — ERP'da ekranlar tez almashadi.
  // Kod bo'lingandan keyin bu yanada muhim: hover chunk'ni oldindan olib
  // keladi, ya'ni bosilganda ekran allaqachon xotirada bo'ladi.
  defaultPreload: 'intent',
  // Ekran chunk'i kelayotgandagi va kelmagandagi holat — HAR marshrutga bir xil
  // qo'llanadi (`components/route-boundary.tsx` dagi batafsil izoh).
  defaultPendingComponent: RoutePending,
  defaultErrorComponent: RouteError,
});

// Router turini global ro'yxatga olish: shundan keyin `<Link to="...">` va
// `navigate({ to })` mavjud bo'lmagan manzilni KOMPILYATSIYA paytida rad etadi.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
